import { Link } from "react-router-dom";
import { IMG } from "@/lib/api";
import { ArrowRight, ArrowUpRight } from "lucide-react";

const BUBBLES = [
  { left: 6, size: 10, dur: 14, delay: 0 }, { left: 14, size: 6, dur: 11, delay: 3 },
  { left: 22, size: 14, dur: 17, delay: 6 }, { left: 31, size: 8, dur: 12, delay: 1.5 },
  { left: 40, size: 5, dur: 10, delay: 7 }, { left: 48, size: 12, dur: 16, delay: 4 },
  { left: 55, size: 7, dur: 13, delay: 9 }, { left: 63, size: 9, dur: 15, delay: 2 },
  { left: 71, size: 5, dur: 11, delay: 5.5 }, { left: 78, size: 13, dur: 18, delay: 8 },
  { left: 85, size: 6, dur: 12, delay: 0.8 }, { left: 92, size: 10, dur: 15, delay: 10 },
  { left: 97, size: 7, dur: 13, delay: 4.5 }, { left: 36, size: 4, dur: 9, delay: 11 },
];

function ChampagneBubbles() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true" data-testid="hero-bubbles">
      {BUBBLES.map((b) => (
        <span
          key={`bubble-${b.left}`}
          className="champagne-bubble"
          style={{ left: `${b.left}%`, width: b.size, height: b.size, animationDuration: `${b.dur}s`, animationDelay: `${b.delay}s` }}
        />
      ))}
    </div>
  );
}

export default function Hero() {
  return (
    <section className="relative w-full" data-testid="hero-section">
      <div className="relative lg:h-[100vh] min-h-[780px] w-full overflow-hidden">
        {/* Beach-vibe gradient — champagne gold up top, warm horizon, teal ocean below */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, #f4d9a8 0%, #e8c79a 18%, #d4a373 34%, #c99a75 46%, #7a8f92 60%, #4a7684 74%, #2c5865 88%, #1a4653 100%)",
          }}
          data-testid="hero-gradient"
        />

        {/* Soft sun glow high in the sky */}
        <div
          className="absolute pointer-events-none"
          style={{
            top: "8%",
            right: "12%",
            width: "min(60vw, 520px)",
            height: "min(60vw, 520px)",
            background:
              "radial-gradient(circle at center, rgba(255,240,205,0.55) 0%, rgba(255,225,180,0.25) 35%, rgba(255,225,180,0) 70%)",
            filter: "blur(4px)",
          }}
        />

        {/* Subtle horizon shimmer to suggest ocean line */}
        <div
          className="absolute inset-x-0 pointer-events-none"
          style={{
            top: "60%",
            height: "2px",
            background:
              "linear-gradient(90deg, transparent 0%, rgba(255,240,205,0.35) 50%, transparent 100%)",
          }}
        />

        {/* Palm tree shadows */}
        <img
          src="/images/hero-palms.png"
          alt=""
          aria-hidden="true"
          className="palm-sway absolute inset-x-0 bottom-0 w-full h-[92%] object-cover object-bottom pointer-events-none select-none"
          style={{ opacity: 0.38, mixBlendMode: "multiply", WebkitMaskImage: "linear-gradient(to top, #000 65%, transparent 100%)", maskImage: "linear-gradient(to top, #000 65%, transparent 100%)" }}
          data-testid="hero-palms"
        />

        {/* Readability wash — bottom-left dark scrim keeps content legible over the gold sky */}
        <div className="absolute inset-y-0 left-0 w-full md:w-3/5 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/65 via-black/25 to-transparent" />

        <ChampagneBubbles />

        <div className="relative h-full container-edge flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10 pt-10 pb-16 md:pb-20">
          <div className="order-2 lg:order-1 max-w-3xl text-white">
            <p
              className="overline"
              style={{ color: "#f4d9a8" }}
              data-testid="hero-overline"
            >
              The House of Excellence · KW Luxury
            </p>
            <h1
              className="hero-headline mt-6 text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-white"
              style={{ fontFamily: "Playfair Display, serif" }}
              data-testid="hero-headline"
            >
              Celebrate <em className="text-[#e8c79a] not-italic font-normal" style={{ fontStyle: "italic" }}>every move</em>.
            </h1>
            <p className="mt-6 max-w-xl text-base md:text-lg text-white/90 font-light leading-relaxed" data-testid="hero-subtitle">
              Champagne &amp; Sunshine — a Los Angeles real estate team. Local experts. Skilled
              negotiators. Trusted advisors. Guiding you through one of the largest and best
              investments you can make.
            </p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link to="/contact?interest=events" className="btn-secondary" data-testid="hero-cta-primary">
                Get our events list <ArrowRight size={16} className="ml-2" />
              </Link>
              <Link
                to="/lifestyles"
                className="btn-outline border-white/70 text-white hover:bg-white hover:text-ocean"
                data-testid="hero-cta-secondary"
              >
                Explore lifestyles
              </Link>
            </div>

            {/* Meet the team inline */}
            <div className="mt-12 flex items-center gap-5 max-w-lg" data-testid="hero-meet-sonia">
              <div className="w-32 h-20 md:w-40 md:h-24 overflow-hidden bg-linen border-2 border-white/40 shrink-0 rounded-md">
                <img
                  src={IMG.agent}
                  alt="Champagne & Sunshine"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: "center 30%" }}
                />
              </div>
              <div>
                <p className="overline text-[#e8c79a]">Meet the team</p>
                <p className="mt-1.5 text-sm md:text-base text-white/90 leading-relaxed">
                  Champagne &amp; Sunshine · Two agents, one phone number.
                </p>
                <Link
                  to="/about"
                  className="mt-1 inline-flex items-center text-sm text-white border-b border-white/60 hover:border-[#e8c79a] hover:text-[#e8c79a] transition-colors"
                  data-testid="hero-meet-sonia-link"
                >
                  About the team <ArrowUpRight size={13} className="ml-1" />
                </Link>
              </div>
            </div>
          </div>

          <div className="order-1 lg:order-2 self-center shrink-0" data-testid="hero-team-photo">
            <div className="team-shimmer animate-[fadeUp_.9s_ease_both]" data-testid="hero-team-shimmer">
            <img
              src="/images/team-circle.webp"
              alt="Champagne & Sunshine Real Estate Team"
              className="block w-[240px] sm:w-[320px] lg:w-[min(38vw,600px)] h-auto drop-shadow-[0_30px_60px_rgba(26,70,83,0.55)]"
            />
            </div>
          </div>
        </div>

        {/* Scroll hint */}
        <div className="absolute bottom-6 right-6 md:right-12 text-white/70 text-xs tracking-[0.3em] uppercase rotate-90 origin-right hidden md:block">
          Scroll
        </div>
      </div>

    </section>
  );
}

export function NeighborhoodMarquee() {
  return (
    <div className="bg-ocean text-white py-2 overflow-hidden" style={{ backgroundColor: "var(--primary)" }} data-testid="hero-marquee">
      <div className="marquee-track">
        {[...Array(2)].map((_, i) => (
          <div key={`marquee-${i}`} className="flex shrink-0 items-center gap-12 px-6">
            {["Beverly Hills", "Santa Monica", "Malibu", "Venice", "Hollywood Hills", "Pacific Palisades", "Brentwood", "Manhattan Beach", "Hermosa Beach", "Redondo Beach", "El Segundo", "Torrance", "Palos Verdes", "Silver Lake", "Calabasas"].map((n) => (
              <span key={`${i}-${n}`} className="text-[11px] md:text-xs tracking-[0.2em] uppercase flex items-center gap-12 text-white/90">
                {n}
                <span className="text-[#e8c79a]">✦</span>
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
